import { LngLatCoordinatePair } from "features/map/event/types"
import type GeometryLayer from "./GeometryLayer.class"
import type { PolygonGeometry } from "./GeometryLinesLayer.class"
import { EditData, GeometryMode, Midpoint } from "./GeometryLayer.class"
import { coordToArrStrict } from "@nova/calculate-geospatial"
import { isLocalDevelopmentEnvironment } from "tools/debugging/module"
import { activateMapDrawingOverlay } from "../useMapDrawingOverlay"
import { GeometryType } from "@nova/constants"
import { useSyncExternalStore } from "react"
import { cloneDeep } from "nodash"
import { Coordinate } from "@nova/protobuf"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import EventEmitter from "events"

const getMidpoints = (points: LngLatCoordinatePair[], isLine: boolean) => {
    if (!points || points.length === 0) return []
    const midpoints = points.map(
        (p, i) =>
            ({
                midpoint: [
                    (p[0] + points[(i + 1) % points.length][0]) / 2,
                    (p[1] + points[(i + 1) % points.length][1]) / 2,
                ],
                first: p,
                second: points[(i + 1) % points.length],
            }) satisfies Midpoint
    )
    if (isLine) midpoints.pop()
    return midpoints
}

export class GeometryDataStore extends EventEmitter {
    #owner = ""
    layer: GeometryLayer | undefined

    points: LngLatCoordinatePair[] = []
    midpoints: {
        midpoint: LngLatCoordinatePair
        first: LngLatCoordinatePair
        second: LngLatCoordinatePair
    }[] = []
    polygon: PolygonGeometry = {
        coordinates: [],
        id: "mock",
        geometryType: GeometryType.Line,
        deckGlIdx: 0,
        idx: 0,
    }
    thinLinePoint: LngLatCoordinatePair | undefined = undefined
    #flush: { current: (store: GeometryDataStore, save: boolean) => void } | undefined
    undoRedo: {
        undo: () => void
        redo: () => void
        undoStack: { do: () => void; undo: () => void }[]
        redoStack: { do: () => void; undo: () => void }[]
    }
    selectedPointIndex = -1
    mode = GeometryMode.LineToPolygon

    undoStack = [] as { do: () => void; undo: () => void }[]
    redoStack = [] as { do: () => void; undo: () => void }[]
    storeListeners = [] as (() => void)[]
    drawing = { acquired: false, cleanup: undefined } as
        | { acquired: false; cleanup: undefined }
        | { acquired: true; cleanup: () => void }

    actionGenerator = {
        add: (point: LngLatCoordinatePair, index?: number) => ({
            do: () => {
                if (index !== undefined) {
                    this.polygon.coordinates.splice(index, 0, {
                        longitude: point[0],
                        latitude: point[1],
                    })
                    this.points.splice(index, 0, point)
                } else {
                    this.polygon.coordinates.push({ longitude: point[0], latitude: point[1] })
                    this.points.push(point)
                }
            },
            undo: () => {
                if (index !== undefined) {
                    this.polygon.coordinates.splice(index, 1)
                    this.points.splice(index, 1)
                } else {
                    this.polygon.coordinates.pop()
                    this.points.pop()
                }
                if (this.points.length === 0) {
                    this.selectedPointIndex = -1
                    this.thinLinePoint = undefined
                }
            },
        }),
        addMany: (points: Coordinate[]) => {
            const len = points.length
            return {
                do: () => {
                    for (const point of points) {
                        this.polygon.coordinates.push(point)
                        this.points.push([point.longitude, point.latitude])
                    }
                },
                undo: () => {
                    for (let i = 0; i < len; i++) {
                        this.polygon.coordinates.pop()
                        this.points.pop()
                    }
                },
            }
        },
        update: (point: LngLatCoordinatePair, idx: number, original: LngLatCoordinatePair) => {
            return {
                do: () => {
                    this.polygon.coordinates[idx] = {
                        longitude: point[0],
                        latitude: point[1],
                    }
                    this.points[idx] = point
                },
                undo: () => {
                    this.polygon.coordinates[idx] = {
                        longitude: original[0],
                        latitude: original[1],
                    }
                    this.points[idx] = original
                },
            }
        },
        delete: (idx: number) => {
            const current = this.points[idx]
            return {
                do: () => {
                    this.polygon.coordinates.splice(idx, 1)
                    this.points.splice(idx, 1)
                    if (this.points.length === 0) {
                        this.selectedPointIndex = -1
                        this.thinLinePoint = undefined
                    }
                },
                undo: () => {
                    this.polygon.coordinates.splice(idx, 0, {
                        longitude: current[0],
                        latitude: current[1],
                    })
                    this.points.splice(idx, 0, current)
                    this.selectedPointIndex = this.points.length - 1
                },
            }
        },
    }

    constructor() {
        super()
        const { undo, redo, undoStack, redoStack } = this
        this.undoRedo = { undo, redo, undoStack, redoStack }
    }

    isLine() {
        return this.polygon.geometryType === GeometryType.Line
    }

    calculateMidpoints() {
        this.midpoints = getMidpoints(this.points, this.isLine())
    }

    addPoint(point: LngLatCoordinatePair, index?: number) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        const action = this.actionGenerator.add(point, index)
        action.do()
        this.undoStack.push(action)
        this.redoStack = []
        this.points = [...this.points]
        this.calculateMidpoints()
        this.selectedPointIndex = this.points.length - 1
        this.layer.setState({
            updateRef: new Date(),
        })
        this.#emitChange()
    }

    addMany(points: Coordinate[]) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        const action = this.actionGenerator.addMany(points)
        action.do()
        this.undoStack.push(action)
        this.redoStack = []
        this.points = [...this.points]
        this.calculateMidpoints()
        this.selectedPointIndex = this.points.length - 1
        this.layer.setState({
            updateRef: new Date(),
        })
        this.#emitChange()
    }

    updateShallow(point: LngLatCoordinatePair, idx: number) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")

        this.polygon.coordinates[idx] = {
            longitude: point[0],
            latitude: point[1],
        }
        this.points[idx] = point
        this.points = [...this.points]
        this.calculateMidpoints()

        this.layer.setState({
            updateRef: new Date(),
        })
    }

    updatePoint(point: LngLatCoordinatePair, idx: number, original: LngLatCoordinatePair) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        const action = this.actionGenerator.update(point, idx, original)
        action.do()
        this.undoStack.push(action)
        this.redoStack = []
        this.points = [...this.points]
        this.selectedPointIndex = idx
        this.calculateMidpoints()
        this.layer.setState({
            updateRef: new Date(),
        })
        this.#emitChange()
    }

    closePoly() {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        this.polygon.geometryType = GeometryType.Polygon
        this.free(true)
    }

    finalizePolygon() {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        this.polygon.geometryType = GeometryType.Polygon
        this.layer.setState({ updateRef: new Date() })
        this.#emitChange()
    }

    updateThinLinePoint(coord?: LngLatCoordinatePair) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        if (coord === this.thinLinePoint) return
        this.thinLinePoint = coord
        this.layer.setState({ updateRef: new Date() })
    }

    deletePoint() {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        if (!this.points.length) return
        if (!this.points[this.selectedPointIndex])
            return console.warn("Tried to delete an invalid point index")
        const idx = this.selectedPointIndex
        const action = this.actionGenerator.delete(idx)
        action.do()
        this.undoStack.push(action)
        this.redoStack = []
        this.points = [...this.points]
        this.calculateMidpoints()
        this.selectedPointIndex = (idx - 1 + this.points.length) % this.points.length
        if (this.points.length === 2 && this.mode === GeometryMode.LineToPolygon) {
            this.polygon.geometryType = GeometryType.Line
        }
        if (this.points.length === 0) {
            this.selectedPointIndex = -1
            this.thinLinePoint = undefined
        }
        this.layer.setState({
            updateRef: new Date(),
        })
        this.#emitChange()
    }

    setSelectedPointIndex(idx: number) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        this.selectedPointIndex = idx
        this.layer?.setState({ updateRef: new Date() })
    }

    undo() {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        if (this.undoStack.length) {
            const action = this.undoStack.pop()!
            action.undo()
            this.points = [...this.points]
            this.calculateMidpoints()
            this.redoStack.push(action)
            this.#emitChange()
            this.layer?.setState({ updateRef: new Date() })
        }
    }

    redo() {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        if (this.redoStack.length) {
            const action = this.redoStack.pop()!
            action.do()
            this.points = [...this.points]
            this.calculateMidpoints()
            this.undoStack.push(action)
            this.#emitChange()
            this.layer?.setState({ updateRef: new Date() })
        }
    }

    #emitChange() {
        const { undo, redo, undoStack, redoStack } = this
        this.undoRedo = { undo: undo.bind(this), redo: redo.bind(this), undoStack, redoStack }
        this.storeListeners.forEach((listener) => listener())
    }

    #subscribeToUpdates(listener: () => void) {
        this.storeListeners = [...this.storeListeners, listener]
        return () => {
            this.storeListeners = this.storeListeners.filter((l) => l !== listener)
        }
    }

    useGeometryData = <T extends keyof GeometryDataStore>(key: T) => {
        const getData = () => {
            return this[key]
        }
        const hook = useSyncExternalStore
        return hook(this.#subscribeToUpdates.bind(this), getData.bind(this))
    }

    setAll(points: Coordinate[]) {
        if (!this.#owner || !this.layer) return console.warn("Store is not acquired")
        this.polygon.coordinates = []
        this.points = []
        this.undoStack = []
        this.redoStack = []
        this.selectedPointIndex = -1
        this.thinLinePoint = undefined
        if (points.length > 0) {
            for (const point of points) {
                this.polygon.coordinates.push(point)
                this.points.push([point.longitude, point.latitude])
            }
            this.points = [...this.points]
            this.calculateMidpoints()
            this.selectedPointIndex = this.points.length - 1
            if (this.mode === GeometryMode.LineToPolygon && this.points.length >= 3) {
                this.polygon.geometryType = GeometryType.Polygon
            }
        } else {
            this.points = []
            this.calculateMidpoints()
            if (this.mode === GeometryMode.LineToPolygon) {
                this.polygon.geometryType = GeometryType.Line
            }
        }
        this.layer.setState({ updateRef: new Date() })
        this.#emitChange()
    }

    exportToPolygons() {
        return this.polygon.coordinates
    }

    isAcquired() {
        return !!this.#owner
    }

    acquire(id: string, data: PolygonGeometry, editData: EditData, mode: GeometryMode) {
        if (!this.#owner || this.#owner === id) {
            this.points = []
            this.midpoints = []
            this.polygon = cloneDeep(data)
            this.points = this.polygon.coordinates?.map(coordToArrStrict) || []
            this.calculateMidpoints()
            this.#flush = editData?.flush
            this.#owner = id
            this.selectedPointIndex = this.points.length - 1
            this.mode = mode
            this.#emitChange()
            return true
        } else {
            if (isLocalDevelopmentEnvironment())
                console.warn("Cannot acquire store when it is already in use")
            return false
        }
    }

    acquireDrawing() {
        if (this.drawing.acquired) {
            if (isLocalDevelopmentEnvironment()) console.warn("Drawing is already acquired")
            return
        }
        const { cleanup } = activateMapDrawingOverlay({
            onFinish: (drawing) => {
                this.addMany(drawing)
            },
            color:
                this.polygon.color !== undefined
                    ? decodeNumberToColor(this.polygon.color)
                    : undefined,
        })
        this.drawing = { acquired: true, cleanup }
    }

    releaseDrawing() {
        if (!this.drawing.acquired) {
            return
        }
        this.drawing.cleanup()
    }

    free(save: boolean) {
        if (!this.#owner) return
        if (this.drawing.acquired) {
            this.drawing.cleanup()
        }
        this.#flush?.current(this, save)
        this.#flush = undefined
        this.layer?.setState({ drawData: undefined })
        this.#owner = ""
        this.points = []
        this.midpoints = []
        this.polygon = {
            coordinates: [],
            id: "mock",
            geometryType: GeometryType.Line,
            deckGlIdx: 0,
            idx: 0,
        }
        this.mode = GeometryMode.LineToPolygon
        this.undoStack = []
        this.redoStack = []
        const { undo, redo, undoStack, redoStack } = this
        this.undoRedo = { undo, redo, undoStack, redoStack }
        this.thinLinePoint = undefined
        this.drawing = { acquired: false, cleanup: undefined }
        this.#emitChange()
    }
}

export const geometryDataStore = new GeometryDataStore()
